// Sentry error tracking service for TellerWand Web App
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import { isProduction, shouldReportToSentry } from "@/utils/env";
import { getSentryDsn } from "@/utils/getSentryDsn";
import { isSentryClientRateLimited, recordSentryClientEvent } from "@/utils/sentryRateLimit";
import {
  shouldDropLocalDevEvent,
  shouldDropNetworkChangeEvent,
  shouldDropNetworkTransportEvent,
  shouldDropRouteNotFoundEvent,
  shouldDropSplitMageOrAuthEvent,
  shouldDropHandledClientNoiseEvent,
  shouldDropAxiosTimeoutNoise,
  shouldDropAutomatedScannerEvent,
  enrichSentryEventWithTrafficContext,
} from "./sentryEventFilters";
import { isChunkLoadError, CHUNK_RELOAD_PENDING_KEY } from "@/utils/lazyWithRetry";
import {
  logMissingSentryDsn,
  logSentryInitDebugPreview,
  logSentryInitSuccess,
  redactTokenQueryFromUrl,
} from "./sentryHelpers";

/**
 * Redact Sensitive Breadcrumbs.
 */
function redactSensitiveBreadcrumbs(
  breadcrumbs: Sentry.Breadcrumb[] | undefined
): Sentry.Breadcrumb[] | undefined {
  if (!breadcrumbs) {
    return breadcrumbs;
  }

  return breadcrumbs.map((breadcrumb) => {
    if (breadcrumb.data) {
      const { data } = breadcrumb;
      Object.keys(data).forEach((key) => {
        if (
          key.toLowerCase().includes("password") ||
          key.toLowerCase().includes("token") ||
          key.toLowerCase().includes("secret") ||
          key.toLowerCase().includes("key")
        ) {
          data[key] = "[REDACTED]";
        }
      });
    }
    return breadcrumb;
  });
}

/**
 * Filter Sentry Transport Console Errors.
 */
function filterSentryTransportConsoleErrors(): void {
  const originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    const [first] = args;
    let message = "";
    if (typeof first === "string") {
      message = first;
    } else if (first instanceof Error) {
      ({ message } = first);
    }
    if (
      message.includes("Sentry") &&
      (message.includes("Failed to send") ||
        message.includes("NetworkError") ||
        message.includes("fetch") ||
        message.includes("CORS") ||
        message.includes("envelope") ||
        message.includes("ingest.sentry.io"))
    ) {
      return;
    }
    originalConsoleError.apply(console, args);
  };
}

/**
 * Process Sentry Before Send.
 */
function processSentryBeforeSend(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): Sentry.ErrorEvent | null {
  if (shouldDropSentryEvent(event, hint)) return null;

  if (isSentryClientRateLimited()) {
    return null;
  }

  if (
    !shouldReportToSentry() &&
    event.tags?.forceSend !== true &&
    event.tags?.forceSend !== "true"
  ) {
    if (import.meta.env.DEV) {
      console.warn("🔍 Sentry event (not sent):", event);
    }
    return null;
  }

  if (import.meta.env.DEV) {
    console.warn(
      "📤 Sending error to Sentry:",
      event.message || event.exception?.values?.[0]?.value
    );
  }

  if (event.tags?.test === true) {
    if (import.meta.env.DEV) {
      console.warn("✅ Test event - will be sent to Sentry");
    }
    return event;
  }

  if (event.request?.url) {
    event.request.url = redactTokenQueryFromUrl(event.request.url);
  }

  recordSentryClientEvent();
  event.breadcrumbs = redactSensitiveBreadcrumbs(event.breadcrumbs);
  return enrichSentryEventWithTrafficContext(event, hint);
}

function getHintExceptionMessage(originalException: unknown): string {
  if (
    originalException &&
    typeof originalException === "object" &&
    "message" in originalException &&
    typeof (originalException as { message?: unknown }).message === "string"
  ) {
    return (originalException as { message: string }).message;
  }
  if (typeof originalException === "string") {
    return originalException;
  }
  return "";
}

function shouldDropSupabaseFetchNoise(combinedMessage: string): boolean {
  return /Failed to fetch/i.test(combinedMessage) && /supabase\.co/i.test(combinedMessage);
}

function shouldDropPendingChunkReload(chunkMessage: string): boolean {
  return (
    typeof sessionStorage !== "undefined" &&
    sessionStorage.getItem(CHUNK_RELOAD_PENDING_KEY) === "true" &&
    isChunkLoadError(chunkMessage)
  );
}

/**
 * Should Drop Sentry Event.
 */
export function shouldDropSentryEvent(
  event:
    | {
        message?: unknown;
        tags?: unknown;
        request?: { url?: unknown; headers?: Record<string, string> };
        user?: { email?: unknown };
        contexts?: Record<string, unknown>;
      }
    | null
    | undefined,
  hint: { originalException?: unknown } | null | undefined
): boolean {
  const requestUrl = typeof event?.request?.url === "string" ? event.request.url : "";
  if (requestUrl.includes("vercel.app")) {
    return true;
  }

  if (event && shouldDropAutomatedScannerEvent(event)) {
    return true;
  }

  const message = typeof event?.message === "string" ? event.message : "";
  if (shouldDropRouteNotFoundEvent(message)) {
    return true;
  }

  if (shouldDropLocalDevEvent(requestUrl, message)) {
    return true;
  }

  if (shouldDropSplitMageOrAuthEvent(hint?.originalException)) {
    return true;
  }

  const exceptionMessage = getHintExceptionMessage(hint?.originalException);
  const combinedNoiseMessage = `${message} ${exceptionMessage}`;
  if (shouldDropNetworkChangeEvent(combinedNoiseMessage)) {
    return true;
  }

  if (shouldDropNetworkTransportEvent(message, hint?.originalException)) {
    return true;
  }

  if (shouldDropHandledClientNoiseEvent(message, hint?.originalException)) {
    return true;
  }

  if (shouldDropAxiosTimeoutNoise(message, hint?.originalException)) {
    return true;
  }

  // Transient browser/auth transport failures (common on /splitmage while refreshing session).
  if (shouldDropSupabaseFetchNoise(combinedNoiseMessage)) {
    return true;
  }

  return shouldDropPendingChunkReload(message || exceptionMessage);
}

/**
 * Build Sentry Init Options.
 */
function buildSentryInitOptions(dsn: string): Sentry.BrowserOptions {
  return {
    dsn,
    environment: shouldReportToSentry() ? "production" : "development",
    tracesSampleRate: shouldReportToSentry() ? 0.1 : 1.0,
    release: import.meta.env.VITE_APP_VERSION || undefined,
    replaysSessionSampleRate: isProduction() ? 0.1 : 0.0,
    replaysOnErrorSampleRate: 1.0,
    transportOptions: {
      useBeacon: true,
    },
    beforeSendTransaction(event) {
      return event;
    },
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.captureConsoleIntegration({
        levels: ["error"],
      }),
    ],
    ignoreErrors: [
      "top.GLOBALS",
      "originalCreateNotification",
      "canvas.contentDocument",
      "MyApp_RemoveAllHighlights",
      "atomicFindClose",
      "fb_xd_fragment",
      "bmi_SafeAddOnload",
      "EBCallBackMessageReceived",
      "conduitPage",
      "ResizeObserver loop limit exceeded",
      /chrome-extension:/,
      /moz-extension:/,
    ],
    beforeSend(event, hint) {
      return processSentryBeforeSend(event, hint);
    },
    initialScope: {
      tags: {
        platform: "web",
      },
    },
  };
}

let sentryInitialized = false;

/**
 * Initializes Sentry error tracking for the application.
 * Should be called after runtime config.js is available.
 * @returns true when Sentry SDK initialized with a DSN
 */
export function initSentry(): boolean {
  if (sentryInitialized) {
    return true;
  }

  const dsn = getSentryDsn();

  if (!dsn) {
    logMissingSentryDsn(import.meta.env.DEV);
    return false;
  }

  logSentryInitDebugPreview(dsn, import.meta.env.DEV);

  try {
    Sentry.init(buildSentryInitOptions(dsn));

    sentryInitialized = true;

    logSentryInitSuccess(dsn, import.meta.env.DEV);

    filterSentryTransportConsoleErrors();

    return true;
  } catch (error) {
    console.warn("❌ Failed to initialize Sentry (non-critical):", error);
    return false;
  }
}

/**
 * Captures an exception manually
 */
export function captureException(error: Error, captureContext?: Sentry.CaptureContext): void {
  Sentry.captureException(error, captureContext);
}

/**
 * Captures a message manually
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = "info"): void {
  Sentry.captureMessage(message, level);
}

/**
 * Sets user context for error tracking
 */
export function setUser(
  user: {
    id?: string;
    email?: string;
    username?: string;
  } | null
): void {
  if (!sentryInitialized) {
    return;
  }
  Sentry.setUser(user);
}

/**
 * Adds breadcrumb for debugging
 */
export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
  Sentry.addBreadcrumb(breadcrumb);
}

/**
 * Sets additional context/tags
 */
export function setTag(key: string, value: string): void {
  Sentry.setTag(key, value);
}

/**
 * Sets additional context
 */
export function setContext(key: string, context: Record<string, unknown>): void {
  Sentry.setContext(key, context);
}
